import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { TablePagination } from '@mui/material';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { getInsiderAssessmentsService } from "../../../services/assessments.service";
import { getOrgModulesService, setModuleVisibilityService } from "../../../services/client.service";
import { analyticsLicensesInfoService } from "../../../services/analyticsLicenses.service";
import { refreshToken } from "../../../services/auth.service";
import { useAuth } from "../../../context/auth";
import { USER_ACCESS_LEVEL, DQ_PRO_PLAN_KEY, TOAST_TYPES } from "../../../utils/constants";
import { useHistory, useLocation } from 'react-router-dom';
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { toast } from "../../../utils/utils";

const formatVisibilityScope = (value) => {
    if (value == null || value === '') return '—';
    const s = String(value).trim();
    return s || '—';
};

const isOrgWideVisibility = (scope) => String(scope || '').toLowerCase() === 'org';

const useStyles = makeStyles((theme) => ({
    tableContent: {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '20px',
    },
    tableHeader: {
        display: 'grid',
        gridTemplateColumns: '1.5fr 2fr 1fr 1fr 1fr 2.2fr',
        padding: '0 20px',
        marginBottom: '12px',
        minWidth: '760px',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { display: 'none' },
    },
    headerCell: {
        fontSize: '15px !important',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: {
            fontSize: '13px !important',
        },
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1.5fr 2fr 1fr 1fr 1fr 2.2fr',
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        padding: '14px 20px',
        alignItems: 'center',
        marginBottom: '10px',
        minWidth: '760px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            padding: '12px 16px',
            gap: 0,
        },
    },
    cellText: {
        fontSize: '15px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px !important',
        },
    },
    cellTextLeft: {
        fontSize: '15px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'left',
        wordBreak: 'break-word',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px !important',
        },
    },
    truncate: {
        display: 'block',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    viewBtn: {
        background: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 20px !important',
        textTransform: 'none !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        boxShadow: 'none !important',
        '&:hover': { background: '#c9541e !important' },
    },
    actionBtn: {
        borderRadius: '50px !important',
        padding: '6px 14px !important',
        textTransform: 'none !important',
        fontWeight: '600 !important',
        fontSize: '12px !important',
        boxShadow: 'none !important',
        whiteSpace: 'nowrap',
    },
    orgLinkBtn: {
        borderColor: '#FF6900 !important',
        color: '#FF6900 !important',
        '&:hover': { borderColor: '#c9541e !important', background: 'rgba(255,105,0,0.06) !important' },
        // Disabled (no license seats / no class pool): same orange, reduced opacity
        '&.Mui-disabled': {
            borderColor: '#FF6900 !important',
            color: '#FF6900 !important',
            opacity: '0.38 !important',
        },
    },
    orgLinkBtnLinked: {
        background: '#2e7d32 !important',
        color: '#fff !important',
        borderColor: '#2e7d32 !important',
        cursor: 'default',
        '&:hover': { background: '#2e7d32 !important' },
        '&.Mui-disabled': {
            background: '#2e7d32 !important',
            color: '#fff !important',
            borderColor: '#2e7d32 !important',
            opacity: 0.92,
        },
    },
    desktopOnly: { [theme.breakpoints.down('sm')]: { display: 'none !important' } },
    mobilePrimary: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '8px',
        },
    },
    mobilePrimaryText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '15px',
            fontWeight: 700,
            color: 'var(--brand-text, #222)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileSubText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '12px',
            color: 'var(--brand-text-muted, #666)',
            marginTop: '2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileActions: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            gap: '6px',
            flexShrink: 0,
            alignItems: 'center',
            flexWrap: 'wrap',
        },
    },
    mobileMetaRow: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            paddingTop: '8px',
            borderTop: '1px solid var(--brand-border, #f0f0f0)',
        },
    },
    mobileMetaTag: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11px',
            color: 'var(--brand-text-muted, #555)',
            background: 'var(--brand-border, #f5f5f5)',
            borderRadius: '20px',
            padding: '2px 8px',
        },
    },
}));

const DQProAssessmentList = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation(); // used to trigger refetch on every navigation to this page
    const { authUser, setAuthUser, setAuthToken } = useAuth();
    const [assessments, setAssessments] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    // null = idle; string = module code currently being linked (only that button shows spinner/fade)
    const [linkingOrgWide, setLinkingOrgWide] = useState(null);
    const [tokenRefreshed, setTokenRefreshed] = useState(false);
    // true  = no license seats available (button disabled, tooltip shown)
    // false = seats available (button enabled)
    // Starts as true so button is disabled until the API confirms seats exist — prevents the
    // enabled→disabled flash on page load.
    const [orgLinkBlocked, setOrgLinkBlocked] = useState(true);

    const isLevel21 = authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    const isLevel22 = authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    const isPaidUser = authUser?.dq_pro_plan?.status === 'active' && authUser?.dq_pro_plan?.plan_key !== DQ_PRO_PLAN_KEY.DEFAULT;
    // Level 21 on default plan sees insider assessments (same as non-org users)
    const isOrgScopedUser = isLevel22 || (isLevel21 && isPaidUser);

    // If the user is level 21 but org_code is not yet present in the token (e.g. right after
    // first assessment creation), refresh the token so the context gets the updated
    // org_code before we attempt to fetch org-scoped data.
    // useEffect(() => {
    //     if (!isOrgScopedUser || authUser?.org_code) {
    //         setTokenRefreshed(true);
    //         return;
    //     }
    //     refreshToken()
    //         .then(response => {
    //             const { auth_token, user } = response?.data || {};
    //             if (auth_token) setAuthToken(auth_token);
    //             if (user) setAuthUser(user);
    //         })
    //         .catch(() => { /* silently fall through; fetch will run with whatever is available */ })
    //         .finally(() => setTokenRefreshed(true));
    // }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // location.key changes on every navigation — ensures list refreshes after assessment creation.
    useEffect(() => {
        fetchAssessments(page + 1, rowsPerPage);

        // When redirected right after creation, the org module record can appear with a short delay.
        // Do one extra delayed refetch, then clear the flag so it doesn't repeat.
        if (location?.state?.refresh) {
            setTimeout(() => fetchAssessments(page + 1, rowsPerPage), 1200);
            history.replace(location.pathname, {});
        }

        // Check if the org has any remaining license capacity for org-wide linking.
        // Disables "Link Org-wide" and shows a tooltip when no seats are available.
        if (isOrgScopedUser && authUser?.org_code) {
            analyticsLicensesInfoService(authUser.org_code)
                .then((res) => setOrgLinkBlocked(computeOrgLinkBlocked(res?.data?.dqp_info)))
                .catch(() => setOrgLinkBlocked(true)); // on error block (backend will surface the real reason)
        }
    }, [page, rowsPerPage, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Normalise an org-module record to the shape expected by the table.
     *  Handles both `module_code`/`module_name` and bare `code`/`name` field variants. */
    const normaliseModule = (m) => ({
        code: m.module_code || m.code,
        full_name: m.module_name || m.full_name || m.name,
        length_questions: m.length_questions || m.no_of_questions || '',
        time_to_complete_minutes: m.time_to_complete_minutes || m.duration || '',
        visibility_scope: m.visibility_scope ?? null,
        createdAt: m.createdAt || null,
    });

    const fetchAssessments = (apiPage, limit) => {
        setLoading(true);

        if (isOrgScopedUser) {
            // Assessments are converted to org modules immediately on completion —
            // there is no "in-progress" insider state. Fetch org modules only.
            getOrgModulesService({ org_code: authUser?.org_code, module_type: 'assessment', page: apiPage, limit })
                .then(response => {
                    // Handle both `{ data: { modules } }` and `{ modules }` response shapes.
                    const payload = response?.data ?? response ?? {};
                    const rawModules = payload?.modules || payload?.assessments || [];
                    const pagination = payload?.pagination;
                    const modules = rawModules.map(normaliseModule);
                    setAssessments(modules);
                    setTotalCount(pagination?.total ?? modules.length);
                    setLoading(false);
                })
                .catch(() => { setAssessments([]); setLoading(false); });
        } else {
            getInsiderAssessmentsService()
                .then(response => {
                    const list = (response?.data?.assessments || response?.assessments || []).map((a) => ({
                        ...a,
                        visibility_scope: a.visibility_scope ?? null,
                    }));
                    list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    setAssessments(list);
                    setTotalCount(list.length);
                    setLoading(false);
                })
                .catch(() => { setAssessments([]); setLoading(false); });
        }
    };


    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
    const handleViewAssessment = (a) => history.push(
        // Straight to the questions preview: the in-between page with the Edit Info
        // and Preview cards was removed (Ian, 13 Oct 2026).
        `/dq_pro/product/view/${a.code}/preview`,
        { assessment: a },
    );

    /**
     * Compute whether the "Link Org-wide" button should be disabled.
     * Accepts the dqp_info object from either the license-info endpoint
     * or the licenses_dq_assess snapshot returned by setModuleVisibility.
     *
     * Block when:
     *  • total = 0     → org has no licenses purchased
     *  • allocated = 0 → no class has a pool assigned yet
     *  • used >= allocated → every allocated seat has been consumed
     *    (compare against allocated, not total — org may have 10 purchased
     *     but only 2 assigned to classes; once those 2 are used all pools
     *     are exhausted even though 8 org-level seats remain unused)
     */
    const computeOrgLinkBlocked = (dqp) => {
        const total     = Number(dqp?.total)     || 0;
        const allocated = Number(dqp?.allocated) || 0;
        const used      = Number(dqp?.used)      || 0;
        return total === 0 || allocated === 0 || used >= allocated;
    };

    const handleLinkOrgWide = async (module) => {
        if (!authUser?.org_code || isOrgWideVisibility(module.visibility_scope)) return;
        setLinkingOrgWide(module.code); // fade only this button
        try {
            const res = await setModuleVisibilityService({
                org_code: authUser.org_code,
                module_code: module.code,
                scope: 'org',
            });
            if (res?.data?.success) {
                // Refresh license state immediately from the response snapshot so
                // the remaining "Link Org-wide" buttons update without a second API call.
                if (res.data.licenses_dq_assess) {
                    setOrgLinkBlocked(computeOrgLinkBlocked(res.data.licenses_dq_assess));
                }
                toast(TOAST_TYPES.SUCCESS, 'Visibility', 'Assessment is now visible org-wide.');
                fetchAssessments(page + 1, rowsPerPage);
                return;
            }
            // Backend license / credit guard: show the specific error message.
            const backendMsg = res?.data?.error_message || res?.error_message || res?.message;
            if (backendMsg) {
                toast(TOAST_TYPES.WARN, 'Visibility', backendMsg);
                return;
            }
            toast(TOAST_TYPES.WARN, 'Visibility', 'Could not set org-wide visibility.');
        } catch (err) {
            // invokeApi already shows a warning toast; avoid double toasts here.
        } finally {
            setLinkingOrgWide(null);
        }
    };

    const orgWideLinkedTitle   = 'Already visible to the entire organization.';
    const orgWideNoLicTitle    = 'No available license seats. Assign licenses to a class first.';
    const orgWideDisabledTitle = (scope) =>
        isOrgWideVisibility(scope) ? orgWideLinkedTitle : (orgLinkBlocked ? orgWideNoLicTitle : '');

    // For non-org path all records are loaded at once, so slice client-side;
    // for org-scoped path rows are already server-paginated.
    const displayed = isOrgScopedUser
        ? assessments
        : assessments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Full timestamp (down to seconds) for the Date Created column.
    // "2026-07-08T13:45:22.000Z" -> "2026-07-08 13:45:22"
    const formatDateTime = (d) => {
        if (!d) return 'N/A';
        const iso = (d instanceof Date) ? d.toISOString() : String(d);
        return iso.replace('T', ' ').split('.')[0].replace('Z', '') || 'N/A';
    };

    return (
        <Box sx={{ paddingTop:'18px' }}>
            <Typography style={{ fontSize: 35, fontWeight: 800, marginBottom: 38, textAlign: 'center', color: 'var(--brand-text, #111)' }}>
                Your Assessments
            </Typography>

            <Box className={classes.tableContent}>
                {displayed.length > 0 && (
                <Box
                    className={classes.tableHeader}
                    style={{ gridTemplateColumns: isOrgScopedUser ? '2fr 1fr 1fr 1fr 1.5fr 2.2fr' : '2fr 1fr 1fr 1.5fr 2.2fr' }}
                >
                    <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Assessment Name</Typography>
                    <Typography className={classes.headerCell}># of Questions</Typography>
                    <Typography className={classes.headerCell}>Time (min)</Typography>
                    {isOrgScopedUser && <Typography className={classes.headerCell}>Visibility</Typography>}
                    <Typography className={classes.headerCell}>Date Created</Typography>
                    <Typography className={classes.headerCell}></Typography>
                </Box>
                )}
                {displayed.length > 0 ? displayed.map((a) => (
                    <Box
                        key={a.code}
                        className={classes.row}
                        style={{ gridTemplateColumns: isOrgScopedUser ? '2fr 1fr 1fr 1fr 1.5fr 2.2fr' : '2fr 1fr 1fr 1.5fr 2.2fr' }}
                    >
                        {/* Desktop cells */}
                        <Box className={classes.desktopOnly} style={{ minWidth: 0 }}>
                            <Typography className={classes.cellTextLeft}>{a.full_name}</Typography>
                            <Tooltip title={a.code}>
                                <Typography className={classes.truncate} style={{ fontSize: 12, color: 'var(--brand-text-muted, #9CA3AF)', textAlign: 'left' }}>({a.code})</Typography>
                            </Tooltip>
                        </Box>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>{a.length_questions}</Typography>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>{a.time_to_complete_minutes || '—'}</Typography>
                        {isOrgScopedUser && (
                            <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>
                                {formatVisibilityScope(a.visibility_scope)}
                            </Typography>
                        )}
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>{formatDateTime(a.createdAt)}</Typography>
                        <Box className={classes.desktopOnly} style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
                            {isOrgScopedUser ? (
                                <Tooltip title={orgWideDisabledTitle(a.visibility_scope)}>
                                    <span>
                                        <Button
                                            className={`${classes.actionBtn} ${
                                                isOrgWideVisibility(a.visibility_scope)
                                                    ? classes.orgLinkBtnLinked
                                                    : classes.orgLinkBtn
                                            }`}
                                            variant={isOrgWideVisibility(a.visibility_scope) ? 'contained' : 'outlined'}
                                            disabled={isOrgWideVisibility(a.visibility_scope) || linkingOrgWide === a.code || !!orgLinkBlocked}
                                            onClick={() => handleLinkOrgWide(a)}
                                        >
                                            {isOrgWideVisibility(a.visibility_scope) ? 'Linked Org-wide' : 'Link Org-wide'}
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : null}
                            <Button className={classes.viewBtn} onClick={() => handleViewAssessment(a)}>
                                View Assessment
                            </Button>
                        </Box>
                        {/* Mobile card */}
                        <Box className={classes.mobilePrimary}>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                                <Typography className={classes.mobilePrimaryText}>{a.full_name}</Typography>
                                <Tooltip title={a.code}>
                                    <Typography className={`${classes.mobileSubText} ${classes.truncate}`}>{a.code}</Typography>
                                </Tooltip>
                            </Box>
                            <Box className={classes.mobileActions}>
                                {isOrgScopedUser && (
                                    <Tooltip title={orgWideDisabledTitle(a.visibility_scope)}>
                                        <span>
                                            <Button
                                                className={`${classes.actionBtn} ${
                                                    isOrgWideVisibility(a.visibility_scope)
                                                        ? classes.orgLinkBtnLinked
                                                        : classes.orgLinkBtn
                                                }`}
                                                variant={isOrgWideVisibility(a.visibility_scope) ? 'contained' : 'outlined'}
                                                disabled={isOrgWideVisibility(a.visibility_scope) || linkingOrgWide === a.code || !!orgLinkBlocked}
                                                onClick={() => handleLinkOrgWide(a)}
                                            >
                                                {isOrgWideVisibility(a.visibility_scope) ? 'Linked Org-wide' : 'Link Org-wide'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                <Button className={classes.viewBtn} onClick={() => handleViewAssessment(a)}>
                                    View Assessment
                                </Button>
                            </Box>
                        </Box>
                        <Box className={classes.mobileMetaRow}>
                            {a.length_questions !== '' && a.length_questions != null && (
                                <span className={classes.mobileMetaTag}>{a.length_questions} questions</span>
                            )}
                            {a.time_to_complete_minutes && (
                                <span className={classes.mobileMetaTag}>{a.time_to_complete_minutes} min</span>
                            )}
                            {isOrgScopedUser && (
                                <span className={classes.mobileMetaTag}>{formatVisibilityScope(a.visibility_scope)}</span>
                            )}
                            {a.createdAt && (
                                <span className={classes.mobileMetaTag}>{formatDateTime(a.createdAt)}</span>
                            )}
                        </Box>
                    </Box>
                )) : (
                    !loading && (
                        <Box style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px 20px',
                            backgroundColor: 'var(--brand-surface, #fff)',
                            borderRadius: '16px',
                            border: '1px dashed rgba(241, 90, 36, 0.3)',
                        }}>
                            <AssessmentIcon style={{ fontSize: 56, color: '#FF6900', opacity: 0.35, marginBottom: 16 }} />
                            <Typography style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand-text, #333)', marginBottom: 8 }}>
                                No assessments yet
                            </Typography>
                            <Typography style={{ fontSize: 14, color: 'var(--brand-text-muted, #888)', textAlign: 'center', maxWidth: 360 }}>
                                You haven't created any assessments. Head to the products page to build your first one.
                            </Typography>
                        </Box>
                    )
                )}

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>

            <LoaderWithBackDrop loading={loading} />
        </Box>
    );
};

export default DQProAssessmentList;
